"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Fragment, useState, useMemo, useEffect, useRef } from "react";
import { useReactTable, getCoreRowModel, getSortedRowModel, getExpandedRowModel, flexRender, } from "@tanstack/react-table";
import { Table, Thead, Tbody, TableContainer, Tr, Th, Td, Flex, Text, useMultiStyleConfig, } from "@chakra-ui/react";
import { SvgSpriteConsumer, IconButton, ButtonVariant, Illustration, } from "../../atoms";
import { selectionColumn, getSortIcon } from "./helpers";
import { Illustrations } from "../../atoms/Illustration";
import { PROD_CDN_BASE_URL } from "../../helpers";
const expanderColumn = {
    id: "expander",
    cell: ({ row }) => (_jsx(IconButton, { "aria-label": "row-expander", variant: ButtonVariant.ICON, size: "md", icon: _jsx(SvgSpriteConsumer, { svgId: `chevron${row.getIsExpanded() ? "Up" : "Down"}24`, size: 24 }), onClick: row.getToggleExpandedHandler() })),
    size: 72,
};
const DataTable = ({ columns, data, enableRowSelection, enableSorting, enableRowExpanding, onRowSelectionChange, renderSubComponent, noResultLabel = "No result found", initialSorting = [], }) => {
    const styles = useMultiStyleConfig("Table");
    const flagFirstRenderRef = useRef(false);
    const [rowSelection, setRowSelection] = useState({});
    const [sorting, setSorting] = React.useState(initialSorting);
    const columnsToDisplay = useMemo(() => {
        const columnsTemp = [...columns];
        if (enableRowSelection) {
            columnsTemp.unshift(selectionColumn);
        }
        if (enableRowExpanding) {
            columnsTemp.push(expanderColumn);
        }
        return columnsTemp;
    }, [columns, enableRowExpanding, enableRowSelection]);
    // columns in tanstack data have always size defined. To know if we are in "auto" default case or not at display time, we override default value with Number.MAX_SAFE_INTEGER constant.
    const table = useReactTable({
        data,
        columns: columnsToDisplay,
        defaultColumn: {
            minSize: 0,
            size: Number.MAX_SAFE_INTEGER,
            maxSize: Number.MAX_SAFE_INTEGER,
        },
        state: {
            rowSelection,
            sorting,
        },
        enableRowSelection,
        enableSorting,
        enableMultiRowSelection: enableRowSelection,
        onSortingChange: setSorting,
        onRowSelectionChange: setRowSelection,
        getRowCanExpand: (() => enableRowExpanding),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
    });
    useEffect(() => {
        if (flagFirstRenderRef.current) {
            const selectedRows = table.getSelectedRowModel();
            const originalSelectedRows = selectedRows.rows.map((row) => row.original);
            onRowSelectionChange === null || onRowSelectionChange === void 0 ? void 0 : onRowSelectionChange({
                selectedRowIndexes: rowSelection,
                selectedRows: originalSelectedRows,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onRowSelectionChange, rowSelection]);
    useEffect(() => {
        flagFirstRenderRef.current = true;
    }, []);
    return (_jsxs(TableContainer, { children: [_jsxs(Table, { children: [_jsx(Thead, { children: table.getHeaderGroups().map((headerGroup) => (_jsx(Tr, { children: headerGroup.headers.map((header) => header.isPlaceholder ? null : (_jsx(Th, Object.assign({ width: header.getSize() === Number.MAX_SAFE_INTEGER
                                    ? "auto"
                                    : `${header.getSize()}px`, sx: header.column.getCanSort()
                                    ? { cursor: "pointer", userSelect: "none" }
                                    : undefined, onClick: () => {
                                    if (header.column.getCanSort()) {
                                        header.column.toggleSorting();
                                    }
                                }, className: `datatable-th-${header.id}` }, { children: _jsxs(Flex, Object.assign({ display: "flex", alignItems: "center", 
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    justifyContent: header.column.columnDef.align }, { children: [flexRender(header.column.columnDef.header, header.getContext()), header.column.getCanSort() ? (_jsx(Flex, Object.assign({ sx: styles.sortIconsContainer }, { children: _jsx(SvgSpriteConsumer, { svgId: getSortIcon(header.column.getIsSorted()), size: 24 }) }))) : null] })) }), header.id))) }, headerGroup.id))) }), _jsx(Tbody, { children: table.getRowModel().rows.map((row) => {
                            var _a;
                            return (_jsxs(Fragment, { children: [_jsx(Tr, Object.assign({ className: `${row.getIsSelected() ? "ds-selected-row" : ""}${row.getIsSelected() && row.getIsExpanded() ? " " : ""}${row.getIsExpanded() ? "ds-expanded-row" : ""}` }, { children: row.getVisibleCells().map((cell) => (_jsx(Td, Object.assign({ width: cell.column.getSize() === Number.MAX_SAFE_INTEGER
                                                ? "auto"
                                                : `${cell.column.getSize()}px`, className: `datatable-td-${cell.column.id}` }, { children: _jsx(Flex, Object.assign({ display: "flex", alignItems: "center", 
                                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                                // @ts-ignore
                                                justifyContent: cell.column.columnDef.align }, { children: flexRender(cell.column.columnDef.cell, cell.getContext()) })) }), cell.id))) })), row.getIsExpanded() && (_jsxs(_Fragment, { children: [_jsx(Tr, Object.assign({ className: "ds-row-expansion" }, { children: _jsx(Td, Object.assign({ colSpan: row.getVisibleCells().length }, { children: (_a = renderSubComponent === null || renderSubComponent === void 0 ? void 0 : renderSubComponent({
                                                        row: row,
                                                    })) !== null && _a !== void 0 ? _a : null })) })), _jsx(Tr, { height: "0 !important" })] }))] }, row.id));
                        }) })] }), !(data === null || data === void 0 ? void 0 : data.length) ? (_jsx(Flex, Object.assign({ sx: styles.notFoundContainer }, { children: _jsxs(Flex, Object.assign({ direction: "column", alignItems: "center" }, { children: [_jsx(Illustration, { width: 140, height: 140, illustrationId: Illustrations.NOT_FOUND, cdnUrl: `${PROD_CDN_BASE_URL}/illustrations/`, alt: "No Data" }), _jsx(Text, Object.assign({ variant: "body.largeBold" }, { children: noResultLabel }))] })) }))) : null] }));
};
DataTable.displayName = "DataTable";
export default DataTable;
//# sourceMappingURL=DataTable.js.map